import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import SectionHeading from "@/components/stori/SectionHeading";

/**
 * Reusable FAQ section. Pass `items: [{ q, a }]`.
 * Caller is responsible for emitting `FAQPage` JSON-LD via <Seo jsonLd={...}>.
 */
export default function FAQBlock({ items, eyebrow = "QUESTIONS", title = "Frequently asked." }) {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section
      data-testid="faq-block"
      className="relative py-24 lg:py-32 border-t border-stori-border"
    >
      <div className="max-w-[900px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow={eyebrow} title={title} align="center" />

        <div className="mt-12 lg:mt-14 space-y-3" itemScope itemType="https://schema.org/FAQPage">
          {items.map((item, i) => {
            const open = openIdx === i;
            return (
              <div
                key={i}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                className={`rounded-xl border bg-stori-surface1 transition-colors ${
                  open ? "border-stori-cyan/40" : "border-stori-border"
                }`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  aria-expanded={open}
                  className="w-full flex items-start justify-between gap-6 p-6 text-left"
                >
                  <span
                    itemProp="name"
                    className="font-heading text-base lg:text-lg font-semibold text-white tracking-tight"
                  >
                    {item.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-stori-muted flex-shrink-0 mt-1 transition-transform ${
                      open ? "rotate-180 text-stori-cyan" : ""
                    }`}
                  />
                </button>
                <div
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                  className={open ? "" : "hidden"}
                >
                  <div className="px-6 pb-6 text-sm text-stori-muted leading-relaxed" itemProp="text">
                    {item.a}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** Build a FAQPage JSON-LD object from a list of { q, a } items. */
export function buildFaqLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}
